import { IntegrationApi } from 'api';
import { BestsellerEventsResponseEventDto } from 'api/common.generated';
import { Checkmark } from 'assets/icons/Checkmark';
import { PromoConditionsAccordionIcon } from 'assets/icons/PromoConditionsAccordionIcon';
import cn from 'classnames';
import { PromoConditionsAccordionProps } from 'features/PromoConditions';
import { PromoConditionsList } from 'features/PromoConditions/components';
import { EventGroups } from 'features/PromoConditions/PromoConditionsAccordion.types';
import { promoAndMatchdayBoost, promoConditionsConfig, promoConditionsValidation } from 'features/PromoConditions/utils';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { MAIN_BALANCE } from 'shared/constants/common';
import { BalanceState, CategoryDataInterface, State } from 'shared/interface/index';

export const PromoConditionsAccordion = ({ promoConditionsState, bets, bonusesActive, freebetsActive, course }: PromoConditionsAccordionProps) => {
	const { t } = useTranslation();
	const allCategories = useSelector<State, CategoryDataInterface[]>((state) => state.categories.all);
	const balanceData = useSelector<State, BalanceState>((state) => state.cpPanel.balanceData);
	const { selectedBalanceType } = balanceData;
	const [events, setEvents] = useState<EventGroups>({ noneInEvents: [], allInEvents: [] }) as any;
	const selectedFreebetId = bets.freebetId ?? 0;
	const selectedFreebetObject = useMemo(
		() => freebetsActive?.find((item) => item.offerFreebetId === +selectedFreebetId),
		[freebetsActive, selectedFreebetId],
	);

	const categoryMap = useMemo(() => {
		const categoriesAndPromotionCategories = { ...allCategories, ...promoAndMatchdayBoost };
		return new Map(Object.values(categoriesAndPromotionCategories).map((item: any) => [item.categoryId, item]));
	}, [allCategories]);

	const accordionElementRef = useRef<any>();
	const selectedData = useMemo(() => {
		return selectedFreebetObject || bonusesActive?.[0];
	}, [selectedFreebetObject, bonusesActive]);

	const isHiddenAccordion = !selectedData || (!selectedFreebetId && selectedBalanceType === MAIN_BALANCE);

	const conditionsConfig = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		return promoConditionsConfig({ selectedData: selectedData! });
	}, [selectedData]);

	const validationResults = useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const validation = promoConditionsValidation({ conditionsConfig, outcomes: bets.outcomes, course, selectedData: selectedData!, bets });
		return validation.reduce((acc, { condition, key, validate }) => {
			if (condition && bets.outcomes?.length) acc[key] = validate();
			return acc;
		}, {} as Record<string, boolean>);
	}, [bets, course, selectedData, conditionsConfig]);

	const areConditionsFulFilled = useMemo(() => {
		if (!bets.outcomes.length) return false;
		return Object.values(validationResults).every(Boolean);
	}, [validationResults]);

	useEffect(() => {
		if (!selectedData) {
			return;
		} else {
			!promoConditionsState.isOpenedPromoAccordion && openAccordion();
		}

		const allInIds = selectedData.betSlipConditions?.outcomeAllInEventIds ?? [];
		const noneInIds = selectedData.betSlipConditions?.outcomeNoneInEventIds ?? [];

		const combinedEvents = [...allInIds, ...noneInIds];

		if (combinedEvents.length === 0) {
			return;
		}
		const fetchMarketingEventsData = async () => {
			const response = await IntegrationApi.getMarketingPrematch(combinedEvents);
			const allEvents = response.data.events;
			if (response && !response.data.isError && response.data.events.length) {
				const includedAllIn = allEvents.filter((event: BestsellerEventsResponseEventDto) => allInIds.includes(event.eventId));
				const includedNoneIn = allEvents.filter((event: BestsellerEventsResponseEventDto) => noneInIds.includes(event.eventId));
				setEvents({ allInEvents: includedAllIn, noneInEvents: includedNoneIn });
			}
		};
		fetchMarketingEventsData();
	}, [selectedData]);

	useEffect(() => {
		promoConditionsState.setOpened(true);
	}, [selectedFreebetId, selectedBalanceType]);

	const openAccordion = () => {
		promoConditionsState.togglePromoAccordion();
	};
	if (isHiddenAccordion) {
		return null;
	}

	const offerStake = selectedData.offerValue ?? selectedData.freebetStake ?? selectedData.currentBonusAmount;

	const offerTitle = `${t(selectedData.nameKey, selectedData.offerName)} - ${offerStake}${t('currency')}`;

	return (
		<div
			ref={(ref) => (accordionElementRef.current = ref)}
			className="relative bg-white dark:bg-dark-300 cursor-pointer  w-full max-h-[142px] mb-3"
			onClick={openAccordion}
		>
			<div className="absolute -top-2 -left-6 -right-6 h-1" style={{ boxShadow: '0px -4px 3px #14141414' }} />
			{areConditionsFulFilled && <div className="absolute h-full rounded-12 -left-2 top-0 bg-success-500 w-[3px]" />}

			<div className="flex justify-between items-center ">
				<div
					className={cn('flex items-center gap-1 font-bold text-primary-500 dark:text-gray-200 text-paragraph-sm truncate mr-2', {
						'text-paragraph-xs': promoConditionsState.isOpenedPromoAccordion,
					})}
				>
					{areConditionsFulFilled && (
						<div className="relative w-5 h-5 flex items-center justify-center shrink-0">
							<Checkmark className=" stroke-primary-500 dark:stroke-gray-200" />
						</div>
					)}
					<span className="line-clamp-1 block text-ellipsis position relative overflow-hidden">{offerTitle}</span>
				</div>
				<PromoConditionsAccordionIcon
					className={cn('transition-all stroke-primary-500 dark:stroke-gray-200', {
						'rotate-180': !promoConditionsState.isOpenedPromoAccordion,
					})}
				/>
			</div>

			<div className="relative overflow-auto max-h-28">
				<AnimatePresence initial={true}>
					{promoConditionsState.isOpenedPromoAccordion && (
						<motion.div
							key="accordion-content"
							className="mt-1"
							initial={{ height: 0, opacity: 0 }}
							animate={{ height: 'auto', opacity: 1 }}
							exit={{ height: 0, opacity: 0 }}
							transition={{ duration: 0.25, ease: 'easeInOut' }}
						>
							<PromoConditionsList
								selectedData={selectedData}
								validation={validationResults}
								conditions={conditionsConfig}
								categoryMap={categoryMap}
								events={events}
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</div>
	);
};
